import { feedKey, PREDICT_FEED, TRANSFERS } from '../lib/feedContext';
import { Decimal } from 'decimal.js';
import type { ExchangeStake, TransferBudget } from '../lib/row';
import { asPolyOrder, type PolyOrder } from '../poly/market';
/**
 * Регуляторы оператора и то, что мы про него измерили.
 *
 * Здесь живёт всё, что нельзя спросить у сервера, потому что это знание о
 * человеке, а не о рынке: сколько у него денег, от какой прибыли он вообще
 * встаёт со стула, какие лимиты стоят на его счетах и — главное — сколько
 * секунд у него уходит от первой ноги до подтверждения второй.
 *
 * Почему темп ИЗМЕРЯЕТСЯ, а не спрашивается. Ответ на вопрос «за сколько вы
 * ставите?» — это оценка себя в спокойный момент, и она врёт в лучшую сторону
 * ровно тогда, когда ошибка дороже всего. Замер берётся по двум подтверждениям
 * самого оператора: он отмечает, что первая нога прошла, и что прошла вторая.
 * Разница между отметками — его темп на этой строке.
 *
 * Стоимость навигации вычитается. Строка с прямой ссылкой на купон и строка, где
 * матч надо искать по сайту конторы, требуют разного времени, и мерить темп
 * человека по второй значило бы записать ему в норму чужую медленную площадку.
 * Поэтому из замера вычитается надбавка той строки, на которой он снят.
 *
 * Хранится в localStorage: вкладка сканера живёт неделями, но переживает не всё,
 * а измеренный темп — это две недели наблюдений, и терять его при перезагрузке
 * значит каждый раз начинать с нуля.
 */

import { create } from 'zustand';

/** Сколько последних попыток держим. Двадцать — это примерно рабочий день. */
export const TEMPO_SAMPLE = 20;

/**
 * Надбавка за навигацию по точности входа, секунды. Не догадка: это разница
 * между «нажал и попал в форму ставки» и «открылась площадка, матч ищи сам».
 * Числа осторожные — завышенная надбавка занижает измеренный темп, то есть
 * делает нас оптимистами относительно человека, а этого делать нельзя.
 */
export const NAVIGATION_COST: Record<string, number> = {
  outcome: 0,
  event: 3,
  section: 9,
};

export const navigationCost = (precision: string | undefined): number =>
  NAVIGATION_COST[precision ?? 'section'] ?? NAVIGATION_COST.section!;

/** Шаг, которым человек режет конторскую ногу. */
export type RoundStep = 1 | 5 | 10 | 100 | 1000;
export const ROUND_STEPS: readonly RoundStep[] = [1, 5, 10, 100, 1000];
export const isRoundStep = (value: unknown): value is RoundStep =>
  ROUND_STEPS.includes(value as RoundStep);
/** Вниз — не вылезем за банк. */
export type RoundDir = 'down' | 'near';
/** Кто получает круглую сумму. */
export type RoundWho = 'book' | 'coarse';

const asStep = (value: unknown, fallback: RoundStep): RoundStep =>
  isRoundStep(value) ? value : fallback;
const asDir = (value: unknown, fallback: RoundDir): RoundDir =>
  value === 'near' || value === 'down' ? value : fallback;
const asWho = (value: unknown, fallback: RoundWho): RoundWho =>
  value === 'coarse' || value === 'book' ? value : fallback;
const asExchange = (value: unknown, fallback: ExchangeStake): ExchangeStake =>
  value === 'share' || value === 'usd' ? value : fallback;

export interface Attempt {
  /** Секунды между подтверждением первой ноги и подтверждением второй. */
  seconds: number;
  /** Сколько из них ушло на дорогу до формы ставки. Вычитается. */
  navigation: number;
  at: number;
}

export interface SettingsState {
  /** Остаток на БК; деньги для встречной ставки на PM сюда не входят. */
  transferBudget: TransferBudget;
  setTransferBudget: (budget: TransferBudget) => void;
  /** Банк в рублях: столько человек готов положить в одну вилку. */
  bank: number;
  /** Порог: от скольки рублей прибыли строка вообще интересна. */
  gate: number;
  /** Курс рубля к доллару. Редактируемый: оператор покупает USDC по своей цене. */
  fx: number;
  /** Округлять конторскую ногу до десятков, сотен или тысяч. */
  roundStep: RoundStep;
  /** Вниз — не вылезем за банк. */
  roundDir: RoundDir;
  /** Кто получает круглую сумму: контора или площадка с грубым шагом. */
  roundWho: RoundWho;
  /** Чем набивают сумму на полике и биржах: баксы или шеры. */
  exchangeStake: ExchangeStake;
  /**
   * Как ставить ногу Polymarket. База — лимитка, как раньше. «По рынку» —
   * доп. режим из студии фильтров (Открытие ставок).
   */
  polyOrder: PolyOrder;
  /** Курс с сервера, чтобы было видно, своё число или наше. */
  fxServer: number | null;
  fxAsOf: string | null;
  fxStale: boolean;
  /**
   * Баланс счёта по конторам: код площадки → сколько на счёте. Правда — касса
   * `/kassa`: сервер кладёт сюда её остатки, карандаш и бот пишут туда же.
   * Обе ноги вилки ужимаются, чтобы ни одна не вышла за свой баланс.
   */
  caps: Record<string, number>;
  /** Валюта баланса. Нет ключа — валюта кассы конторы (старые записи). */
  capsCur: Record<string, 'RUB' | 'USD'>;
  /**
   * Фрибет SNR по конторам: код → сколько можно поставить не из кармана.
   * Лента считает ногу этой конторы как фрибет и не выходит за эту сумму.
   */
  freebets: Record<string, number>;
  /** Валюта фрибета. Нет ключа — валюта кассы конторы. */
  freebetsCur: Record<string, 'RUB' | 'USD'>;
  /** Лимит из купона конторы, в её валюте. До перезагрузки, на сервер не уходит. */
  couponCaps: Record<string, number>;
  /** Замеры темпа, последние сверху не важно — считается медиана. */
  attempts: Attempt[];
  /**
   * Счёт дня: сколько вилок начато (первая нога) и сколько доведено до
   * последней. День — календарный: вчерашние попытки в «сегодня» не живут.
   * Раньше оба числа росли одним действием и никогда не сбрасывались — счётчик
   * всегда показывал «N из N» и не значил ничего (07.08, владелец).
   */
  taken: number;
  tried: number;
  /** День, к которому относится счёт, `YYYY-MM-DD` по локальному времени. */
  day: string;
  /**
   * Открывать все конторы вилки одним нажатием первой кнопки. Требует
   * разрешения на всплывающие окна: браузер пускает вторую вкладку только с ним.
   */
  pairOpen: boolean;
  /** Показывать ли свёрнутый хвост мелочи. */
  tailOpen: boolean;
  /**
   * Кошелёк Polymarket (0x…) — по нему карточка видит налив шаров. Хранится
   * отдельным ключом и в `settingsRecord` не входит: на сервер не уходит.
   */
  polyWallet: string;

  setBank: (bank: number) => void;
  setGate: (gate: number) => void;
  setFx: (fx: number) => void;
  setRound: (step: RoundStep, dir: RoundDir, who: RoundWho) => void;
  setExchangeStake: (as: ExchangeStake) => void;
  setPolyOrder: (order: PolyOrder) => void;
  noteServerFx: (value: number | null, asOf: string | null, stale: boolean) => void;
  setCap: (source: string, value: number | null, cur?: 'RUB' | 'USD') => void;
  /** Остатки, которые сервер свёл из кассы. Заменяют карту целиком. */
  setBalances: (caps: Record<string, number>, capsCur: Record<string, 'RUB' | 'USD'>) => void;
  setFreebet: (source: string, value: number | null, cur?: 'RUB' | 'USD') => void;
  noteAttempt: (seconds: number, navigation: number) => void;
  /** Вилка начата: подтверждена первая нога. */
  noteTried: () => void;
  /** Вилка доведена: подтверждена последняя нога. */
  noteTaken: () => void;
  setPairOpen: (on: boolean) => void;
  setTailOpen: (open: boolean) => void;
  setPolyWallet: (wallet: string) => void;
  /** Принять регуляторы с сервера при входе. Пустые поля не трогаются. */
  hydrate: (saved: Saved) => void;
}

export const SETTINGS_KEY = feedKey('forq.next.settings');
const KEY = SETTINGS_KEY;
const WALLET_KEY = feedKey('forq.next.polyWallet');

function loadWallet(): string {
  try {
    return localStorage.getItem(WALLET_KEY) ?? '';
  } catch {
    return '';
  }
}

interface Saved {
  transferBudget?: TransferBudget;
  bank?: number;
  gate?: number;
  fx?: number;
  roundStep?: RoundStep;
  roundDir?: RoundDir;
  roundWho?: RoundWho;
  exchangeStake?: ExchangeStake;
  polyOrder?: PolyOrder;
  caps?: Record<string, number>;
  capsCur?: Record<string, 'RUB' | 'USD'>;
  freebets?: Record<string, number>;
  freebetsCur?: Record<string, 'RUB' | 'USD'>;
  attempts?: Attempt[];
  taken?: number;
  tried?: number;
  day?: string;
  pairOpen?: boolean;
  /** Каким поколением замера накоплены `attempts`. См. `MEASURE`. */
  measure?: number;
}

/**
 * Поколение замера темпа. Отметки прошлых поколений выбрасываются при загрузке.
 *
 * До второго поколения темп записывался на нажатие «открыть и скопировать», то
 * есть на НАМЕРЕНИЕ поставить. Человек, ткнувший две кнопки из любопытства и не
 * поставивший ничего, получал измеренный темп 56 с — и лента по этому
 * выдуманному числу раздавала строкам пометки «может не дожить» (08.08,
 * владелец). Оставить такие замеры рядом с честными нельзя: медиана считается по
 * всем, и одна выдумка тянет за собой все будущие выводы. Пустая выборка честнее
 * подкрашенной — темп снова «не измерен», пока не появится настоящая ставка.
 */
const MEASURE = 2;

/** Сегодняшний день по локальному времени человека: счёт дня живёт в его сутках. */
function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

/** Балансы с сервера: число или строка с точкой. Мусор и ноль выбрасываются. */
function capsOf(value: unknown): Record<string, number> | undefined {
  if (value === null || typeof value !== 'object') return undefined;
  const out: Record<string, number> = {};
  for (const [code, raw] of Object.entries(value as Record<string, unknown>)) {
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (Number.isFinite(n) && n > 0) out[code] = n;
  }
  return out;
}

function capsCurOf(value: unknown): Record<string, 'RUB' | 'USD'> | undefined {
  if (value === null || typeof value !== 'object') return undefined;
  const out: Record<string, 'RUB' | 'USD'> = {};
  for (const [code, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === 'RUB' || raw === 'USD') out[code] = raw;
  }
  return out;
}

/** Всё, что режет ногу по конторе, одним куском для `buildRow`. */
export function capsInput(
  state: Pick<SettingsState, 'caps' | 'capsCur' | 'couponCaps' | 'freebets' | 'freebetsCur'>,
): {
  caps: Record<string, number>;
  capsCur: Record<string, 'RUB' | 'USD'>;
  couponCaps: Record<string, number>;
  freebets: Record<string, number>;
  freebetsCur: Record<string, 'RUB' | 'USD'>;
} {
  return {
    caps: state.caps,
    capsCur: state.capsCur,
    couponCaps: state.couponCaps,
    freebets: state.freebets,
    freebetsCur: state.freebetsCur,
  };
}

function transferBudgetOf(value: TransferBudget | undefined): TransferBudget {
  if (!value) return { amount: '100', currency: 'USD' };
  const currency = value.currency === 'RUB' ? 'RUB' : 'USD';
  try {
    const amount = new Decimal(value.amount.replace(',', '.'));
    return { currency, amount: amount.isFinite() && amount.gt(0)
      ? amount.toDecimalPlaces(2, Decimal.ROUND_DOWN).toString() : '' };
  } catch {
    return { currency, amount: '' };
  }
}

function load(): Saved {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? {} : (JSON.parse(raw) as Saved);
  } catch {
    // Испорченная запись не имеет права мешать открыть сканер: настройки —
    // удобство, лента — работа.
    return {};
  }
}

/** Exact persisted shape, also used by Studio's multi-store save. */
export function settingsRecord(state: SettingsState): Saved {
  return {
    ...(TRANSFERS ? { transferBudget: state.transferBudget } : {}),
    bank: state.bank,
    gate: state.gate,
    fx: state.fx,
    roundStep: state.roundStep,
    roundDir: state.roundDir,
    roundWho: state.roundWho,
    exchangeStake: state.exchangeStake,
    polyOrder: state.polyOrder,
    caps: state.caps,
    capsCur: state.capsCur,
    freebets: state.freebets,
    freebetsCur: state.freebetsCur,
    attempts: state.attempts,
    taken: state.taken,
    tried: state.tried,
    day: state.day,
    pairOpen: state.pairOpen,
    measure: MEASURE,
  };
}

function save(state: SettingsState): void {
  try {
    const body = settingsRecord(state);
    localStorage.setItem(KEY, JSON.stringify(body));
  } catch {
    // Приватный режим и переполненное хранилище — не повод падать.
  }
}

/**
 * Медиана замеров за вычетом дороги, секунды. `null` — замеров ещё нет.
 *
 * Именно `null`, а не «пусть будет одиннадцать». Темп решает, показывать ли
 * строке пометку «может не дожить», и подставленное число раздавало бы эту
 * пометку по чужой мерке — то есть пугало бы одних и успокаивало других.
 */
export function tempoOf(attempts: readonly Attempt[]): number | null {
  const clean = attempts
    .map((a) => Math.max(1, a.seconds - a.navigation))
    .sort((a, b) => a - b);
  if (clean.length === 0) return null;
  const middle = Math.floor(clean.length / 2);
  return clean.length % 2 === 1
    ? clean[middle]!
    : Math.round((clean[middle - 1]! + clean[middle]!) / 2);
}

const saved = load();

export const useSettings = create<SettingsState>((set, get) => ({
  transferBudget: transferBudgetOf(saved.transferBudget),
  setTransferBudget: (budget) => {
    set({ transferBudget: transferBudgetOf(budget) });
    save(get());
  },
  bank: numberOr(saved.bank, 10000),
  // Предикт-вилка — центы на шар и банк в долларах: 300 ₽ «беру от» прятали всю ленту.
  gate: typeof saved.gate === 'number' && Number.isFinite(saved.gate) ? saved.gate : PREDICT_FEED ? 0 : 300,
  roundStep: asStep(saved.roundStep, 100),
  roundDir: asDir(saved.roundDir, 'down'),
  roundWho: asWho(saved.roundWho, 'book'),
  exchangeStake: asExchange(saved.exchangeStake, 'usd'),
  polyOrder: asPolyOrder(saved.polyOrder),
  // Курс до ответа сервера — прошлый известный или ноль. Ноль означает «курса
  // нет», и строка с биржевой ногой в этот момент честно показывает прочерк.
  fx: numberOr(saved.fx, 0),
  fxServer: null,
  fxAsOf: null,
  fxStale: true,
  caps: capsOf(saved.caps) ?? {},
  capsCur: capsCurOf(saved.capsCur) ?? {},
  freebets: capsOf(saved.freebets) ?? {},
  freebetsCur: capsCurOf(saved.freebetsCur) ?? {},
  couponCaps: {},
  attempts: saved.measure === MEASURE ? (saved.attempts ?? []) : [],
  // Вчерашний счёт в «сегодня» не переезжает: перезагрузка на следующий день
  // начинает день с нуля, как и обещает слово в шапке.
  taken: saved.day === today() ? (saved.taken ?? 0) : 0,
  tried: saved.day === today() ? (saved.tried ?? 0) : 0,
  day: today(),
  // Включено по умолчанию: вилка ниже 7% живёт 10–15 секунд, а два отдельных
  // нажатия стоят лишнего возврата к ленте между ногами. Браузер, не пустивший
  // вторую вкладку, говорит об этом сразу — карточка показывает отказ, а не
  // делает вид, что контора открыта.
  pairOpen: saved.pairOpen ?? true,
  tailOpen: false,
  polyWallet: loadWallet(),

  setBank: (bank) => {
    set({ bank: numberOr(bank, get().bank) });
    save(get());
  },
  setGate: (gate) => {
    const floor = -Math.round(Math.max(0, get().bank) * 0.05);
    set({ gate: Math.max(floor, Math.round(gate)) });
    save(get());
  },
  setFx: (fx) => {
    set({ fx: numberOr(fx, get().fx) });
    save(get());
  },
  setRound: (roundStep, roundDir, roundWho) => {
    set({
      roundStep: asStep(roundStep, get().roundStep),
      roundDir: asDir(roundDir, get().roundDir),
      roundWho: asWho(roundWho, get().roundWho),
    });
    save(get());
  },
  setExchangeStake: (exchangeStake) => {
    set({ exchangeStake: asExchange(exchangeStake, get().exchangeStake) });
    save(get());
  },
  setPolyOrder: (polyOrder) => {
    set({ polyOrder: asPolyOrder(polyOrder, get().polyOrder) });
    save(get());
  },

  // Серверный курс не затирает введённый оператором: он покупает валюту не у
  // регулятора. Подставляется только там, где своего числа ещё нет.
  noteServerFx: (value, asOf, stale) => {
    set({ fxServer: value, fxAsOf: asOf, fxStale: stale });
    if (value !== null && get().fx <= 0) {
      set({ fx: value });
      save(get());
    }
  },

  setCap: (source, value, cur) => {
    const caps = { ...get().caps };
    const capsCur = { ...get().capsCur };
    if (value === null || !Number.isFinite(value) || value <= 0) {
      delete caps[source];
      delete capsCur[source];
    } else {
      caps[source] = value;
      if (cur !== undefined) capsCur[source] = cur;
    }
    set({ caps, capsCur });
    save(get());
  },

  setBalances: (caps, capsCur) => {
    set({ caps: capsOf(caps) ?? {}, capsCur: capsCurOf(capsCur) ?? {} });
    save(get());
  },

  setFreebet: (source, value, cur) => {
    const freebets = { ...get().freebets };
    const freebetsCur = { ...get().freebetsCur };
    if (value === null || !Number.isFinite(value) || value <= 0) {
      delete freebets[source];
      delete freebetsCur[source];
    } else {
      freebets[source] = value;
      if (cur !== undefined) freebetsCur[source] = cur;
    }
    set({ freebets, freebetsCur });
    save(get());
  },

  noteAttempt: (seconds, navigation) => {
    const attempt: Attempt = { seconds, navigation, at: Date.now() };
    set({ attempts: [...get().attempts, attempt].slice(-TEMPO_SAMPLE) });
    save(get());
  },

  noteTried: () => {
    const fresh = get().day === today();
    set({ tried: (fresh ? get().tried : 0) + 1, taken: fresh ? get().taken : 0, day: today() });
    save(get());
  },

  noteTaken: () => {
    const fresh = get().day === today();
    set({ taken: (fresh ? get().taken : 0) + 1, tried: fresh ? get().tried : 0, day: today() });
    save(get());
  },

  setPairOpen: (pairOpen) => {
    set({ pairOpen });
    save(get());
  },

  setTailOpen: (tailOpen) => set({ tailOpen }),

  setPolyWallet: (wallet) => {
    const polyWallet = wallet.trim();
    set({ polyWallet });
    try {
      if (polyWallet === '') localStorage.removeItem(WALLET_KEY);
      else localStorage.setItem(WALLET_KEY, polyWallet);
    } catch {
      // Приватный режим — налив просто не отслеживается.
    }
  },

  // Серверный профиль главнее локального, и это не про доверие к серверу, а
  // про смысл: локальное — это память браузера, серверное — память человека.
  // Он входит на чужой машине именно затем, чтобы работать своими числами.
  hydrate: (incoming) => {
    set({
      ...(TRANSFERS && incoming.transferBudget !== undefined
        ? { transferBudget: transferBudgetOf(incoming.transferBudget) } : {}),
      bank: numberOr(incoming.bank, get().bank),
      gate: typeof incoming.gate === 'number' ? Math.max(0, Math.round(incoming.gate)) : get().gate,
      fx: numberOr(incoming.fx, get().fx),
      roundStep: asStep(incoming.roundStep, get().roundStep),
      roundDir: asDir(incoming.roundDir, get().roundDir),
      roundWho: asWho(incoming.roundWho, get().roundWho),
      exchangeStake: asExchange(incoming.exchangeStake, get().exchangeStake),
      polyOrder: asPolyOrder(incoming.polyOrder, get().polyOrder),
      caps: capsOf(incoming.caps) ?? get().caps,
      // Сервер прислал балансы без валют — это старые «макс. ставки» в валюте кассы.
      capsCur: capsCurOf(incoming.capsCur) ?? (incoming.caps !== undefined ? {} : get().capsCur),
      freebets: incoming.freebets !== undefined ? (capsOf(incoming.freebets) ?? {}) : get().freebets,
      freebetsCur:
        incoming.freebetsCur !== undefined
          ? (capsCurOf(incoming.freebetsCur) ?? {})
          : incoming.freebets !== undefined
            ? {}
            : get().freebetsCur,
      attempts: incoming.attempts ?? get().attempts,
      taken: incoming.taken ?? get().taken,
      tried: incoming.tried ?? get().tried,
    });
    save(get());
  },
}));
